<div class="lab-results-dialog">
  <!-- Header -->
  <div class="dialog-header">
    <div class="header-content">
      <div class="title-section">
        <h2 mat-dialog-title>
          <mat-icon class="title-icon">science</mat-icon>
          Lab Results
        </h2>
        <div class="order-info">
          <span class="order-number">#{{ labOrder.orderNumber || labOrder.id.substring(0, 8) }}</span>
          <span *ngIf="requisitionNumber" class="requisition-number">
            <mat-icon>receipt</mat-icon>
            Req: {{ requisitionNumber }}
            <mat-icon class="info-icon"
              matTooltip="Requisition Number: This is the unique identifier assigned by Lancet Laboratories to your lab order. It's used to track your order throughout the testing process and can be referenced when contacting the laboratory about your results."
              matTooltipPosition="above">info</mat-icon>
          </span>
          <span *ngIf="caps.patientFileLink && patient" class="patient-name clickable-patient-name"
            (click)="navigateToPatientFile(patient.id)">{{ patient.name
            }} {{ patient.surname }}</span>
          <!-- Read-only: demographics exactly as the laboratory sent them. There is no patient record. -->
          <ng-container *ngIf="readOnly">
            <span class="patient-name">{{ unlinkedSubjectName }}</span>
            <span class="unlinked-chip" matTooltip="This result is not attached to a patient file.">Unlinked</span>
            <span *ngIf="unlinkedResult?.reportType" class="unlinked-chip">{{ unlinkedResult?.reportType }}</span>
          </ng-container>
        </div>
        <!--
            The parties the LAB named, with the lab's own raw role code. Absent entirely — no
            container, no label — when the row carries none, which is every pre-existing row.
            Read-only mode is unaffected: this is display only and gates nothing.
        -->
        <div class="vendor-doctor-badges" *ngIf="vendorDoctorBadges.length">
          <span class="vendor-doctor-badge" *ngFor="let badge of vendorDoctorBadges"
            [matTooltip]="badge.tooltip">
            <span class="vendor-doctor-badge-label">{{ badge.label }}</span>
            <span class="vendor-doctor-badge-role">{{ badge.role }}</span>
          </span>
        </div>
      </div>
      <div class="header-actions">
        <button mat-icon-button *ngIf="caps.flag" (click)="toggleFlag()" [matTooltip]="isFlagged ? 'Unflag for follow-up' : 'Flag for follow-up'">
          <mat-icon [class.flagged-icon]="isFlagged">flag</mat-icon>
        </button>
        <button mat-icon-button (click)="downloadLabResultsPdf()" [matTooltip]="'Download Lab Results as PDF'" [disabled]="downloadDisabled">
          <mat-icon>download</mat-icon>
        </button>
        <button mat-icon-button (click)="onClose()" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="dialog-content" mat-dialog-content>
    <!-- Tabbed Layout Container -->
    <mat-tab-group class="lab-results-tabs">
      <!-- Lab Results Tab -->
      <mat-tab label="Lab Results">
        <div class="tab-content">
          <!-- Loading State -->
          <div *ngIf="loading" class="loading-container">
            <mat-spinner diameter="40"></mat-spinner>
            <p>Loading lab results...</p>
          </div>

          <!-- Error State -->
          <div *ngIf="error && !loading" class="error-container">
            <mat-icon class="error-icon">error</mat-icon>
            <p>{{ error }}</p>
            <button mat-raised-button color="primary" (click)="loadLabResults()">
              <mat-icon>refresh</mat-icon>
              Retry
            </button>
          </div>

          <!-- Results Content -->
          <div *ngIf="!loading && !error && showResultsBody" class="results-container">
            <!-- Summary -->
            <div class="results-summary">
              <div class="summary-card">
                <div class="summary-item">
                  <mat-icon>science</mat-icon>
                  <div class="summary-content">
                    <span class="summary-label">Total Tests</span>
                    <span class="summary-value">{{ totalTests }}</span>
                  </div>
                </div>
                <div class="summary-item">
                  <mat-icon [class]="abnormalResults > 0 ? 'warning-icon' : 'normal-icon'">
                    {{ abnormalResults > 0 ? 'warning' : 'check_circle' }}
                  </mat-icon>
                  <div class="summary-content">
                    <span class="summary-label">Abnormal Results</span>
                    <span class="summary-value" [class]="abnormalResults > 0 ? 'abnormal' : 'normal'">
                      {{ abnormalResults }}
                    </span>
                  </div>
                </div>
                <div class="summary-item">
                  <mat-icon>schedule</mat-icon>
                  <div class="summary-content">
                    <span class="summary-label">{{ readOnly ? 'Received' : 'Completed' }}</span>
                    <span class="summary-value">{{ labOrder.resultDate | date: 'yyyy-MM-dd HH:mm' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Test Filter -->
            <div class="test-filter-wrapper">
              <mat-form-field appearance="outline" class="test-filter-field">
                <mat-label>Search Tests</mat-label>
                <input matInput [(ngModel)]="testFilterValue" (keyup)="applyTestFilter($event)"
                  placeholder="Search for a test" #input>
                <mat-icon matSuffix *ngIf="!testFilterValue">search</mat-icon>
                <mat-icon matSuffix *ngIf="testFilterValue" (click)="clearTestFilter()"
                  class="clickable">close</mat-icon>
                <mat-hint>Search by test name, code, or abbreviation</mat-hint>
              </mat-form-field>
            </div>

            <!-- Specimens -->
            <ng-container *ngFor="let specimen of labResults.specimens; let specimenIndex = index">
              <div class="specimens-container" *ngIf="specimen.hasMatchingTests">
                <div class="specimen-header">
                  <h3>
                    <mat-icon>{{ specimen.specimenIcon }}</mat-icon>
                    Specimen {{ specimenIndex + 1 }}
                    <span *ngIf="specimen.department" class="department-badge">{{ specimen.department }}</span>
                    <span *ngIf="specimen.isMicrobiology" class="microbiology-badge">Microbiology</span>
                  </h3>
                  <div class="specimen-info">
                    <div class="specimen-details">
                      <span *ngIf="specimen.number" class="specimen-number">
                        <mat-icon>qr_code</mat-icon>
                        #{{ specimen.number }}
                        <mat-icon class="info-icon"
                          matTooltip="Specimen Reference Number: This unique identifier is used by the laboratory to track your sample throughout the testing process. It helps ensure accurate results and proper sample handling."
                          matTooltipPosition="above">info</mat-icon>
                      </span>
                      <span *ngIf="specimen.collectionDate" class="collection-date">
                        <mat-icon>schedule</mat-icon>
                        Collected: {{ specimen.collectionDate | date: 'yyyy-MM-dd' }} at {{ '1991-01-01T' +
                        specimen.collectionTime | date: 'HH:mm' }}
                      </span>
                      <!-- Microbiology specific fields -->
                      <span *ngIf="specimen.isMicrobiology && specimen.source" class="specimen-source">
                        <mat-icon>water_drop</mat-icon>
                        Source: {{ specimen.source }}
                      </span>
                      <span *ngIf="specimen.isMicrobiology && specimen.description" class="specimen-description">
                        <mat-icon>description</mat-icon>
                        {{ specimen.description }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Procedures -->
                <ng-container *ngFor="let procedure of specimen.procedures; let procedureIndex = index">
                  <div class="procedures-container" *ngIf="procedure.hasMatchingTests">
                    <div class="procedure-card" [ngClass]="{'microbiology-card': specimen.isMicrobiology}">
                      <div class="procedure-header">
                        <h4>
                          <mat-icon>{{ specimen.isMicrobiology ? 'science' : 'assignment' }}</mat-icon>
                          {{ procedure.procedureName || procedure.procedureCode }}
                        </h4>
                        <div class="procedure-meta" *ngIf="procedure.originalTest">
                          <span class="original-test">
                            <mat-icon>check_circle</mat-icon>
                            Originally Ordered
                          </span>
                        </div>
                      </div>

                      <!-- Test Results - Non-Microbiology Template -->
                      <div class="test-results" *ngIf="!specimen.isMicrobiology">
                        <ng-container *ngFor="let test of procedure.tests; let testIndex = index">
                          <div class="test-result" *ngIf="test.matchesFilter">
                            <div class="test-header">
                              <div class="test-name">
                                <span class="test-code">{{ test.testCode }}</span>
                                <span class="test-name-text">{{ test.testName }}</span>
                                <span *ngIf="test.testAbbreviation && test.testAbbreviation !== test.testName"
                                  class="test-abbreviation">
                                  ({{ test.testAbbreviation }})
                                </span>
                              </div>
                              <div class="test-status-top-right" [ngClass]="test.flagClass">
                                <mat-icon [class]="test.flagClass + '-icon'">
                                  {{ test.flagIcon || 'check_circle' }}
                                </mat-icon>
                                <span>{{ test.flagText || 'Normal' }}</span>
                              </div>
                            </div>
                            <div class="test-result-section">
                              <div class="test-value-large">
                                <span class="value">{{ test.displayValue ?? test.value }}</span>
                                <span class="units" *ngIf="test.units">{{ test.units }}</span>
                              </div>
                              <div class="test-range-small" *ngIf="test.range">
                                <span class="range-label">Ref:</span>
                                <span class="range-value">{{ test.range }}</span>
                              </div>
                            </div>
                            <div class="test-details">
                              <div class="quality-control" *ngIf="test.enteredBy || test.verifiedBy || test.dictatedBy">
                                <div class="qc-info">
                                  <span *ngIf="test.enteredBy" class="qc-item">
                                    <mat-icon>edit</mat-icon>
                                    Entered by: {{ test.enteredBy }}
                                  </span>
                                  <span *ngIf="test.verifiedBy" class="qc-item">
                                    <mat-icon>verified</mat-icon>
                                    Verified by: {{ test.verifiedBy }}
                                  </span>
                                  <span *ngIf="test.dictatedBy" class="qc-item">
                                    <mat-icon>record_voice_over</mat-icon>
                                    Dictated by: {{ test.dictatedBy }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </ng-container>
                      </div>

                      <!-- Test Results - Microbiology Template -->
                      <div class="test-results microbiology-results" *ngIf="specimen.isMicrobiology">
                        <ng-container *ngFor="let test of procedure.tests; let testIndex = index">
                          <div class="test-result microbiology-test-result" *ngIf="test.matchesFilter">
                            <div class="test-header">
                              <div class="test-name">
                                <span class="test-code">{{ test.testCode }}</span>
                                <span class="test-name-text">{{ test.displayName }}</span>
                              </div>
                            </div>
                            <div class="test-result-section microbiology-result-section">
                              <div class="microbiology-value">
                                <span class="value">{{ test.displayValue }}</span>
                              </div>
                              <!-- Microbiology tests may not have units/range -->
                              <div class="test-range-small" *ngIf="test.range">
                                <span class="range-label">Ref:</span>
                                <span class="range-value">{{ test.range }}</span>
                              </div>
                            </div>
                            <!-- Microbiology comments -->
                            <div class="test-comment" *ngIf="test.commentText">
                              <div class="comment-header">
                                <mat-icon>comment</mat-icon>
                                <span>Comment</span>
                              </div>
                              <div class="comment-text">{{ test.commentText }}</div>
                            </div>
                            <div class="test-details">
                              <div class="quality-control" *ngIf="test.enteredBy || test.verifiedBy || test.dictatedBy">
                                <div class="qc-info">
                                  <span *ngIf="test.enteredBy" class="qc-item">
                                    <mat-icon>edit</mat-icon>
                                    Entered by: {{ test.enteredBy }}
                                  </span>
                                  <span *ngIf="test.verifiedBy" class="qc-item">
                                    <mat-icon>verified</mat-icon>
                                    Verified by: {{ test.verifiedBy }}
                                  </span>
                                  <span *ngIf="test.dictatedBy" class="qc-item">
                                    <mat-icon>record_voice_over</mat-icon>
                                    Dictated by: {{ test.dictatedBy }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </ng-container>
                      </div>
                    </div>
                  </div>
                </ng-container>
              </div>
            </ng-container>
          </div>

          <!--
            Read-only unlinked document with nothing renderable.
            `hasResults === false` is a REAL report that simply carries no discrete tests
            (comment-only blood gas reports), and `hasResults === null` means nobody parsed it.
            Neither is a failure, so both render the document header rather than an empty pane.
          -->
          <div *ngIf="!loading && !error && readOnly && !showResultsBody" class="no-results-container">
            <mat-icon class="no-results-icon">{{ unlinkedState === 'not-parsed' ? 'help_outline' : 'description' }}</mat-icon>
            <h3>{{ unlinkedStateTitle }}</h3>
            <p>{{ unlinkedStateDetail }}</p>
            <div class="unlinked-document-details">
              <div><strong>Patient (as sent by the lab):</strong> {{ unlinkedSubjectName }}</div>
              <div *ngIf="unlinkedResult?.demographics?.dateOfBirth">
                <strong>Date of birth:</strong> {{ unlinkedResult?.demographics?.dateOfBirth }}
              </div>
              <div *ngIf="unlinkedResult?.demographics?.idNumber">
                <strong>ID number:</strong> {{ unlinkedResult?.demographics?.idNumber }}
              </div>
              <div *ngIf="!unlinkedResult?.demographics?.idNumber && unlinkedResult?.demographics?.passportNumber">
                <strong>Passport:</strong> {{ unlinkedResult?.demographics?.passportNumber }}
              </div>
              <div *ngIf="unlinkedResult?.specimenNumber">
                <strong>Specimen:</strong> {{ unlinkedResult?.specimenNumber }}
              </div>
              <div *ngIf="unlinkedResult?.reportType">
                <strong>Report type:</strong> {{ unlinkedResult?.reportType }}
              </div>
              <div *ngIf="unlinkedResult?.specimenStatus">
                <strong>Specimen status:</strong> {{ unlinkedResult?.specimenStatus }}
              </div>
              <div *ngIf="unlinkedResult?.receivedAt">
                <strong>Received:</strong> {{ unlinkedResult?.receivedAt | date: 'yyyy-MM-dd HH:mm' }}
              </div>
            </div>
            <button mat-raised-button color="primary" (click)="downloadLabResultsPdf()" [disabled]="downloadDisabled">
              <mat-icon>download</mat-icon>
              Download report PDF
            </button>
          </div>

          <!-- No Results State -->
          <div *ngIf="!loading && !error && !readOnly && !hasResultsFlag" class="no-results-container">
            <mat-icon class="no-results-icon">science</mat-icon>
            <h3>No Results Available</h3>
            <p>Lab results have not been received yet for this order.</p>
          </div>
        </div>
      </mat-tab>

      <!--
        AI Assistant Tab. Hidden entirely in read-only mode rather than shown disabled:
        `sendMessage` posts `patientId` and there is no patient, and every message action
        inside it (email, WhatsApp, add-to-timeline) is patient-bound or outbound.
      -->
      <mat-tab label="Cura AI Assistant" *ngIf="caps.curaAssistant">
        <!-- The CURA dialog's conversation (src/_cura-window.scss .cura-conversation), in its
             blue, inside this tab: quick actions and the template on the left, the messages as
             bubbles, the composer at the foot. Every action is the same as before. -->
        <div class="tab-content cura-tokens cura-conversation">
          <div class="ai-locked" *ngIf="lockProcessing && !isPremium">
            <mat-icon>error</mat-icon>
            <p class="p-l-10">You do not have enough AI tokens to continue utilizing AI features. Please
              recharge your token balance by navigating to settings and selecting the "Subscriptions &amp; Usage" tab.</p>
          </div>

          <div class="cura-body" *ngIf="!lockProcessing">
            <aside class="cura-side">
              <div class="side-head">Quick actions</div>
              <div class="side-actions">
                <button mat-stroked-button class="quick-action" (click)="generatePatientSummary()"
                  [disabled]="selectedNoteTypeId !== null">
                  <mat-icon>summarize</mat-icon>Patient summary
                </button>
                <button mat-stroked-button class="quick-action" (click)="explainAbnormalResults()"
                  [disabled]="selectedNoteTypeId !== null">
                  <mat-icon>warning</mat-icon>Explain abnormal results
                </button>
                <button mat-stroked-button class="quick-action" (click)="generateFollowUpRecommendations()"
                  [disabled]="selectedNoteTypeId !== null">
                  <mat-icon>recommend</mat-icon>Follow-up recommendations
                </button>
              </div>

              <div class="note-type-selection-container" *ngIf="forCuraNoteTypes.length > 0">
                <div class="side-head">Template</div>
                <button type="button" class="cura-select full" [matMenuTriggerFor]="labTemplateMenu"
                  matTooltip="Write the reply to a note template (optional)">
                  <span class="cura-select-text" [class.placeholder]="!selectedNoteTypeId">{{ selectedNoteTypeLabel || 'No template' }}</span>
                  <mat-icon class="cura-select-clear" *ngIf="selectedNoteTypeId" matTooltip="Clear selection"
                    (click)="$event.stopPropagation(); clearNoteTypeSelection()">close</mat-icon>
                  <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                </button>
              </div>
            </aside>

            <mat-menu #labTemplateMenu="matMenu" class="cura-menu">
              <div class="cura-menu-label">Template</div>
              <button mat-menu-item [class.selected]="!selectedNoteTypeId" (click)="onNoteTypeSelected(null)">
                <mat-icon>{{ !selectedNoteTypeId ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                <span>None</span>
              </button>
              <button mat-menu-item *ngFor="let noteType of forCuraNoteTypes"
                [class.selected]="selectedNoteTypeId === noteType.id" (click)="onNoteTypeSelected(noteType.id)">
                <mat-icon>{{ selectedNoteTypeId === noteType.id ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                <span>{{ noteType.label }}</span>
              </button>
            </mat-menu>

            <mat-menu #labQuickMenu="matMenu" class="cura-menu">
              <div class="cura-menu-label">Quick actions</div>
              <button mat-menu-item (click)="generatePatientSummary()" [disabled]="selectedNoteTypeId !== null">
                <mat-icon>summarize</mat-icon><span>Patient summary</span>
              </button>
              <button mat-menu-item (click)="explainAbnormalResults()" [disabled]="selectedNoteTypeId !== null">
                <mat-icon>warning</mat-icon><span>Explain abnormal results</span>
              </button>
              <button mat-menu-item (click)="generateFollowUpRecommendations()" [disabled]="selectedNoteTypeId !== null">
                <mat-icon>recommend</mat-icon><span>Follow-up recommendations</span>
              </button>
            </mat-menu>

            <div class="cura-main">
              <!-- A narrow tab: the side pane gives way to this strip. -->
              <div class="cura-strip">
                <button type="button" class="cura-select" [matMenuTriggerFor]="labQuickMenu">
                  <mat-icon class="cura-select-icon">bolt</mat-icon>
                  <span class="cura-select-text">Quick actions</span>
                  <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                </button>
                <button type="button" class="cura-select" *ngIf="forCuraNoteTypes.length > 0"
                  [matMenuTriggerFor]="labTemplateMenu" matTooltip="Write the reply to a note template (optional)">
                  <span class="cura-select-text" [class.placeholder]="!selectedNoteTypeId">{{ selectedNoteTypeLabel || 'No template' }}</span>
                  <mat-icon class="cura-select-clear" *ngIf="selectedNoteTypeId" matTooltip="Clear selection"
                    (click)="$event.stopPropagation(); clearNoteTypeSelection()">close</mat-icon>
                  <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                </button>
              </div>

              <div class="cura-messages" #messagesContainer>
                <div class="token-balance-warning" *ngIf="!isPremium && tokenBalance && tokenBalance <= 100">
                  <mat-icon>warning</mat-icon>
                  <p class="p-l-10">Your token balance is currently R{{ tokenBalance }}. Please
                    recharge soon to ensure you are able to continue utilizing AI features.</p>
                </div>

                <!-- Cura's welcome, as its first bubble, until there is a conversation. -->
                <div class="cura-msg theirs" *ngIf="!messages?.length">
                  <div class="bubble tail">
                    <span class="sender">Cura</span>
                    <p>Ask me for a patient-friendly summary of these lab results, to explain the abnormal
                      results, or anything about the findings.</p>
                    <p>Use the quick actions or type your own message.</p>
                  </div>
                </div>

                <ng-container *ngIf="messages?.length">
                  <div *ngFor="let message of messages" class="cura-msg"
                    [class.mine]="message.isSent" [class.theirs]="!message.isSent">
                    <div class="bubble tail">
                      <span *ngIf="!message.isSent" class="sender">{{ message.sender?.name }}
                        {{message.sender?.surname}}</span>
                      <span class="message-text" [innerHTML]="message.message | formatMessage"
                        *ngIf="!message.isEditing"></span>
                      <mat-form-field appearance="outline" class="edit-field" *ngIf="message.isEditing">
                        <mat-label>Message</mat-label>
                        <textarea matInput appFindReplace [(ngModel)]="message.message" rows="20"></textarea>
                      </mat-form-field>
                      <div class="turn-status" *ngIf="message.isLoading">
                        <mat-spinner mode="indeterminate" diameter="18"></mat-spinner>
                        <span>Working on it…</span>
                      </div>
                    </div>
                    <div class="bubble-tools" *ngIf="!message.isLoading && !message.isSent">
                      <ng-container *ngIf="!message.isEditing">
                        <mat-icon class="pointer" (click)="toggleEditing(message)"
                          matTooltip="Edit the content of this message">edit</mat-icon>
                        <mat-icon class="pointer" (click)="copyMessage(message)" matTooltip="Copy this message">
                          {{message.isCopied ? 'check' : 'content_copy'}}
                        </mat-icon>
                        <mat-icon class="pointer" (click)="addMessageToTimeline(message)"
                          matTooltip="Add this message to the patient timeline">post_add</mat-icon>

                        <ng-container *ngIf="canSign; else cantSign">
                          <mat-icon matTooltip="Download this message as PDF" [matMenuTriggerFor]="downloadMenu"
                            class="pointer" (click)="$event.stopPropagation()">file_download</mat-icon>

                          <mat-menu #downloadMenu="matMenu" class="cura-menu cura-menu-form">
                            <div class="cura-menu-label">Download as PDF</div>
                            <mat-checkbox [(ngModel)]="includeSignatureOnPdf" class="checkbox"
                              (click)="$event.stopPropagation()">
                              {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                            </mat-checkbox>
                            <mat-form-field *ngIf="hasSignaturePermission" appearance="outline" class="full-width"
                              (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                              <mat-label>Select Doctor's Signature</mat-label>
                              <mat-select [(ngModel)]="selectedDoctor" panelClass="cura-select-panel">
                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                  {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                </mat-option>
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="full-width"
                              (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                              <mat-label>Document Date</mat-label>
                              <input matInput [matDatepicker]="pdfDatePicker" [(ngModel)]="pdfDate" placeholder="DD/MM/YYYY">
                              <mat-datepicker-toggle matSuffix [for]="pdfDatePicker"></mat-datepicker-toggle>
                              <mat-datepicker #pdfDatePicker></mat-datepicker>
                            </mat-form-field>

                            <button mat-raised-button class="tone-button full-width"
                              (click)="downloadAsPdf($event, message)">
                              Download PDF <mat-icon>file_download</mat-icon>
                            </button>
                          </mat-menu>
                        </ng-container>
                        <ng-template #cantSign>
                          <mat-icon matTooltip="Download this message as PDF" (click)="downloadAsPdf($event, message)"
                            class="pointer">file_download</mat-icon>
                        </ng-template>

                        <mat-icon class="pointer" matTooltip="Send this via email" [matMenuTriggerFor]="emailMenu" *ngIf="caps.email"
                          (click)="$event.stopPropagation()">mail</mat-icon>

                        <mat-icon class="pointer" matTooltip="Send this via WhatsApp" appWhatsappDialogTrigger
                          [patientId]="patient?.id" [patient]="patient"
                          [initialMessage]="getMessagePlainText(message)" svgIcon="whatsapp"></mat-icon>

                        <mat-menu #emailMenu="matMenu" class="cura-menu cura-menu-form email-menu-panel">
                          <div mat-menu-content (click)="$event.stopPropagation()">
                            <div class="cura-menu-label">Send by email</div>

                            <mat-checkbox [(ngModel)]="sendingDetails.patchPatient" class="checkbox"
                              (change)="patchPatientEmailDetails()">Send To Patient</mat-checkbox>
                            <mat-checkbox *ngIf="canSign" [(ngModel)]="includeSignatureOnPdf" class="checkbox"
                              (click)="$event.stopPropagation()">
                              {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                            </mat-checkbox>

                            <mat-form-field *ngIf="hasSignaturePermission" appearance="outline" class="full-width"
                              (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                              <mat-label>Select Doctor's Signature</mat-label>
                              <mat-select [(ngModel)]="selectedDoctor" panelClass="cura-select-panel">
                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                  {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                </mat-option>
                              </mat-select>
                            </mat-form-field>
                            <app-recipient-search [patient]="patient" label="Quick Select Recipient"
                              placeholder="Search for recipient..." (recipientSelected)="onRecipientSelected($event)">
                            </app-recipient-search>
                            <mat-form-field appearance="outline" class="full-width"
                              (keydown)="handleMenuKeydown($event)">
                              <mat-label>Recipient Name</mat-label>
                              <input matInput [(ngModel)]="sendingDetails.name">
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="full-width">
                              <mat-label>Recipient Email</mat-label>
                              <input matInput [(ngModel)]="sendingDetails.email">
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="full-width"
                              (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                              <mat-label>Document Date</mat-label>
                              <input matInput [matDatepicker]="pdfDatePickerEmail" [(ngModel)]="pdfDate" placeholder="DD/MM/YYYY">
                              <mat-datepicker-toggle matSuffix [for]="pdfDatePickerEmail"></mat-datepicker-toggle>
                              <mat-datepicker #pdfDatePickerEmail></mat-datepicker>
                            </mat-form-field>
                            <button mat-raised-button class="tone-button full-width"
                              [disabled]="!sendingDetails.email" (click)="emailMessage(message)"
                              matTooltip="Send to this email address">
                              <mat-icon>send</mat-icon> Send Email
                            </button>
                          </div>
                        </mat-menu>
                      </ng-container>
                      <ng-container *ngIf="message.isEditing">
                        <mat-icon class="pointer" (click)="toggleEditing(message)"
                          matTooltip="Save the changes to this message">save</mat-icon>
                      </ng-container>
                    </div>
                  </div>
                </ng-container>
                <p class="retention-note" *ngIf="messages?.length">
                  These messages are not retained: copy anything important before closing. Cura can make
                  mistakes, so check important information.
                </p>
              </div>

              <div class="cura-composer">
                <div class="compose-row">
                  <input class="compose-text" [(ngModel)]="newMessage" [placeholder]="messagePlaceholder"
                    (keyup.enter)="sendMessage()" [disabled]="loading" aria-label="Message to Cura" />
                  <button type="button" class="send" (click)="sendMessage()" [disabled]="loading" aria-label="Send">
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>

  <!-- Footer -->
  <!-- <div class="dialog-footer" mat-dialog-actions>
    <button mat-button (click)="onClose()">Close</button>
    
  </div> -->
</div>

<ng-template #addNoteDialog>
  <h1 mat-dialog-title>Note Details</h1>
  <div mat-dialog-content>
    <div class="field-column">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Date</mat-label>
        <input matInput [matDatepicker]="dateDatePicker" [(ngModel)]="addingNote.date" placeholder="DD/MM/YYYY"
          [max]="todayDate">
        <mat-datepicker-toggle matSuffix [for]="dateDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #dateDatePicker></mat-datepicker>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{addingNote.isProcedure ? 'Procedure Title' : 'Note Title'}}</mat-label>
        <input matInput [(ngModel)]="addingNote.title">
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Notes</mat-label>
        <textarea matInput cdkTextareaAutosize="true" rows="10" [(ngModel)]="addingNote.notes"></textarea>
      </mat-form-field>

      <div class="toggle-container" *ngIf="!stateUnit">
        <mat-slide-toggle [(ngModel)]="addingNote.paidCash" (change)="toggleCodeStatus(addingNote, 'up-front')"
          color="primary">Paid Up Front</mat-slide-toggle>
        <mat-slide-toggle [(ngModel)]="addingNote.noCharge" color="primary"
          (change)="toggleCodeStatus(addingNote, 'no-charge')">No Charge</mat-slide-toggle>
      </div>

      <app-procedure-code-selector [initialCodes]="addingNote.procedureCodes"
        (codesChanged)="onSelectedProcedureCodesChanged($event, addingNote)"
        [patient]="patient"></app-procedure-code-selector>

      <app-file-upload [fileObjects]="[]" [allowMultiple]="true" [allowedFileTypes]="'image/*, application/pdf'"
        title="Note Media" (fileUpload)="onNoteMediaUpload($event)" (fileDelete)="onNoteMediaDelete($event)"
        (filesEdited)="onFilesEdited($event)" [cameraEnabled]="true" [enableEditing]="true">
      </app-file-upload>

      <p class="hint"><strong>Note: </strong>MedicAI can make mistakes. We advise that you check the content
        of the output carefully before accepting it into the patient file.</p>
    </div>
  </div>
  <div mat-dialog-actions class="dialog-actions">
    <button mat-button [mat-dialog-close]="null">Cancel</button>
    <button mat-raised-button color="primary" [disabled]="!addingNote.date || !addingNote.title"
      [mat-dialog-close]="addingNote">Save</button>
  </div>
</ng-template>